/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * OwnershipRelationsPage
 * ----------------------
 * Dedicated, routed home for a single group's "Ownership Relation Access"
 * configuration. Extracted out of the group create/edit modal (Task 6b) after
 * live user feedback: this belongs on its own page reached from a group's card,
 * with breadcrumbs, not buried inside a form.
 *
 * Route: /dashboard/applications/:id/groups/:groupId/ownership-relations
 *
 * There is no wrapping form / Save button here, so this page AUTOSAVES: every
 * time OwnershipRelationSection reports a change via onChange, we optimistically
 * update local state and immediately PATCH /arcee/groups/update/ with
 * { id, ownership_relation_fields, ownership_relation_configured_fields }. A
 * small save-status indicator keeps the admin honest about what is actually
 * persisted (Saving… / Saved / a prominent, retryable error on failure).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import OwnershipRelationSection from '../components/applications/OwnershipRelationSection';
import ProgressBarDialog from '../components/common/ProgressBar';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret
});

export default function OwnershipRelationsPage() {
    const { theme } = useTheme();
    const { id: applicationId, groupId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [group, setGroup] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [selectedFieldIds, setSelectedFieldIds] = useState([]);
    // Durable "explicitly touched" marker set — see the doc comment at the
    // top of OwnershipRelationSection.js for why this exists: without it,
    // a tree an admin deliberately emptied out looks identical to a tree
    // that was never opened, and silently gets re-defaulted to all-enabled
    // on the next visit to this page.
    const [configuredFieldIds, setConfiguredFieldIds] = useState([]);

    // Autosave status: 'idle' | 'saving' | 'saved' | 'error'.
    const [saveState, setSaveState] = useState('idle');
    // The ids of the most recent PATCH attempt, kept so an error state can be
    // retried without needing the admin to re-toggle anything.
    const pendingIdsRef = useRef(null);
    // Monotonic request counter so only the latest autosave gets to set the
    // final displayed status (rapid successive toggles must not let a slow
    // earlier response clobber a newer one's state).
    const saveSeqRef = useRef(0);
    // Serializes the actual network requests. Each persist() call chains
    // onto this promise instead of firing immediately, so at most one PATCH
    // is ever in flight: the next save's request is not issued until the
    // previous one has fully resolved (success or failure). This guarantees
    // the server always receives writes in the order they were issued, which
    // saveSeqRef alone cannot do -- saveSeqRef only orders which response is
    // allowed to update the UI, it does nothing to stop two PATCHes from
    // being in flight at once and racing each other to the server. Without
    // this, an older (stale) request could still land on the server after a
    // newer one if responses arrive out of order, leaving the server holding
    // stale data while the UI happily shows "Saved".
    const persistChainRef = useRef(Promise.resolve());

    useEffect(() => {
        let cancelled = false;
        const fetchGroup = async () => {
            setLoading(true);
            setNotFound(false);
            try {
                // No single-group GET endpoint exists — fetch the list and pick
                // ours out by id (same call OrganizationGroupsPage already makes).
                const resp = await apiHelper.apiCall(`/arcee/groups/?application_id=${applicationId}`);
                if (!resp.ok) throw new Error('Failed to load group');
                const data = await resp.json();
                const groups = Array.isArray(data) ? data : (data.results || []);
                const match = groups.find((g) => String(g.id) === String(groupId));
                if (cancelled) return;
                if (!match) {
                    setGroup(null);
                    setNotFound(true);
                } else {
                    setGroup(match);
                    setSelectedFieldIds(match.ownership_relation_fields || []);
                    setConfiguredFieldIds(match.ownership_relation_configured_fields || []);
                }
            } catch (e) {
                if (!cancelled) {
                    console.error('Failed to load group for ownership relations', e);
                    setNotFound(true);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchGroup();
        return () => { cancelled = true; };
    }, [applicationId, groupId]);

    const persist = useCallback((nextIds, nextConfiguredIds) => {
        pendingIdsRef.current = { nextIds, nextConfiguredIds };
        const reqId = ++saveSeqRef.current;
        // Reflect "saving" as soon as the call is queued, even though the
        // actual request may wait behind an earlier one still in flight --
        // from the admin's perspective their change is not yet persisted
        // either way, so this stays honest.
        setSaveState('saving');

        const sendRequest = async () => {
            try {
                const resp = await apiHelper.apiCall('/arcee/groups/update/', {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        id: groupId,
                        ownership_relation_fields: nextIds,
                        ownership_relation_configured_fields: nextConfiguredIds,
                    }),
                });
                if (!resp.ok) throw new Error('Failed to save');
                // Only the newest queued save may resolve the visible status.
                if (reqId === saveSeqRef.current) setSaveState('saved');
            } catch (e) {
                console.error('Failed to save ownership relations', e);
                if (reqId === saveSeqRef.current) setSaveState('error');
            }
        };

        // Chain onto the previous save regardless of whether it succeeded or
        // failed, so this request is only ever issued after the previous one
        // has fully settled -- never two PATCHes in flight at once. That
        // makes network response ordering irrelevant: the server always
        // processes writes in the exact order the admin made them.
        const next = persistChainRef.current.then(sendRequest, sendRequest);
        persistChainRef.current = next;
        return next;
    }, [groupId]);

    // onChange from OwnershipRelationSection: optimistically reflect the change
    // in the UI immediately, then autosave it. We intentionally do NOT roll the
    // optimistic UI back on failure — that would leave the open tree modal's own
    // per-edge overrides disagreeing with the master list. Instead the failure
    // is surfaced prominently and is retryable, so the admin always knows the
    // shown state is not yet persisted.
    const handleChange = useCallback((nextIds, nextConfiguredIds) => {
        setSelectedFieldIds(nextIds);
        setConfiguredFieldIds(nextConfiguredIds);
        persist(nextIds, nextConfiguredIds);
    }, [persist]);

    const handleRetry = useCallback(() => {
        if (pendingIdsRef.current !== null) {
            const { nextIds, nextConfiguredIds } = pendingIdsRef.current;
            persist(nextIds, nextConfiguredIds);
        }
    }, [persist]);

    const goBack = () => navigate(`/dashboard/applications/${applicationId}/groups`);

    return (
        <div className="d-flex flex-column w-100" style={{ background: '#f8fafc', minHeight: '100vh', overflowX: 'hidden' }}>
            {/* Header */}
            <div className="w-100 px-3 py-3 px-md-4 py-md-4 bg-white border-bottom d-flex flex-wrap align-items-center justify-content-between gap-3 shadow-sm">
                <div className="d-flex align-items-center gap-3">
                    <button
                        className="btn btn-light rounded-circle shadow-sm"
                        onClick={goBack}
                        title="Back to Groups"
                        style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <i className="bi bi-arrow-left"></i>
                    </button>
                    <div>
                        <h4 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                            <i className="bi bi-diagram-3" style={{ color: theme.primary }}></i>
                            Ownership Relations{group ? ` — ${group.name}` : ''}
                        </h4>
                        <small className="text-muted d-none d-md-block">
                            Control which foreign-key chains resolve row ownership for this group's members
                        </small>
                    </div>
                </div>

                {/* Save-status indicator */}
                <div className="d-flex align-items-center" style={{ minHeight: 24 }}>
                    {saveState === 'saving' && (
                        <small className="text-muted d-flex align-items-center gap-1">
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            Saving…
                        </small>
                    )}
                    {saveState === 'saved' && (
                        <small className="text-success d-flex align-items-center gap-1">
                            <i className="bi bi-check-circle-fill"></i>
                            Saved
                        </small>
                    )}
                    {saveState === 'error' && (
                        <div className="d-flex align-items-center gap-2 text-danger">
                            <small className="d-flex align-items-center gap-1">
                                <i className="bi bi-exclamation-triangle-fill"></i>
                                Changes not saved
                            </small>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger py-0"
                                onClick={handleRetry}
                            >
                                Retry
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="container-fluid p-3 p-md-4" style={{ maxWidth: 900 }}>
                {loading ? (
                    <ProgressBarDialog open={true} text_message="Loading group..." />
                ) : notFound ? (
                    <div className="card border-0 shadow-sm" style={{ borderRadius: theme.cardRadius }}>
                        <div className="card-body text-center text-muted py-5 px-3">
                            <div className="mb-3 display-4 text-light"><i className="bi bi-exclamation-circle"></i></div>
                            <h5>Group Not Found</h5>
                            <p className="small text-muted">
                                This group may have been deleted, or the link is no longer valid.
                            </p>
                            <button className="btn btn-primary mt-2" onClick={goBack}>Back to Groups</button>
                        </div>
                    </div>
                ) : (
                    <div className="card border-0 shadow-sm" style={{ borderRadius: theme.cardRadius }}>
                        <div className="card-body p-3 p-md-4">
                            <OwnershipRelationSection
                                applicationId={applicationId}
                                groupId={groupId}
                                selectedFieldIds={selectedFieldIds}
                                configuredFieldIds={configuredFieldIds}
                                onChange={handleChange}
                                apiHelper={apiHelper}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
